import React, { useState } from 'react';
import './CreateProviderTypeForm.css';
import { create_providertype } from '../../../services/AuthServices';

const CreateProviderTypeForm = ({ onSuccess, existingTypes = [] }) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    type: '',
    description: '',
    isActive: true,
    serviceName: '',
    serviceDescription: '',
    isServiceActive: true
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.type.trim()) {
      errors.type = 'Type name is required';
    } else if (existingTypes.some(type => type.type.toLowerCase() === formData.type.toLowerCase())) {
      errors.type = 'This provider type already exists';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    // If service name is provided, service description is required
    if (formData.serviceName.trim() && !formData.serviceDescription.trim()) {
      errors.serviceDescription = 'Service description is required when service name is provided';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Format the data according to the API requirements
      const apiData = {
        type: formData.type.trim(),
        description: formData.description.trim(),
        isActive: formData.isActive,
        serviceName: formData.serviceName.trim() || null,
        serviceDescription: formData.serviceDescription.trim() || null,
        isServiceActive: formData.serviceName.trim() ? formData.isServiceActive : null
      };

      // Call the service function
      const result = await create_providertype([apiData]); // Wrap in array as API expects array

      setSuccessMessage('Provider type created successfully!');
      
      // Reset form
      resetForm();
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(result);
      }
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowModal(false);
      }, 2000);
      
    } catch (err) {
      setError(err.message || 'An error occurred while creating provider type');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      type: '',
      description: '',
      isActive: true,
      serviceName: '',
      serviceDescription: '',
      isServiceActive: true
    });
    setFormErrors({});
  };

  const handleOpenModal = () => {
    resetForm();
    setShowModal(true);
    setError('');
    setSuccessMessage('');
  };

  return (
    <>
      <button 
        className="btn-create-provider-type"
        onClick={handleOpenModal}
      >
        Create New Provider Type
      </button>

      {showModal && (
        <div className="create-modal-overlay">
          <div className="create-modal">
            <div className="create-modal-header">
              <h2>Create New Provider Type</h2>
              <button 
                className="close-btn"
                onClick={() => setShowModal(false)}
                disabled={isLoading}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="create-provider-form">
              <div className="form-group">
                <label htmlFor="type" className="required">
                  Type Name
                </label>
                <input
                  type="text"
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  placeholder="e.g., admin, manager, staff"
                  className={formErrors.type ? 'error' : ''}
                  disabled={isLoading}
                />
                {formErrors.type && (
                  <span className="error-message">{formErrors.type}</span>
                )}
                <small className="field-hint">
                  Unique identifier for this provider type
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="description" className="required">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the purpose and permissions of this provider type"
                  rows="3"
                  className={formErrors.description ? 'error' : ''}
                  disabled={isLoading}
                />
                {formErrors.description && (
                  <span className="error-message">{formErrors.description}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="serviceName">
                    Service Name (Optional)
                  </label>
                  <input
                    type="text"
                    id="serviceName"
                    name="serviceName"
                    value={formData.serviceName}
                    onChange={handleInputChange}
                    placeholder="e.g., Admin Portal, User Management"
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="serviceDescription">
                    Service Description
                  </label>
                  <textarea
                    id="serviceDescription"
                    name="serviceDescription"
                    value={formData.serviceDescription}
                    onChange={handleInputChange}
                    placeholder="Describe the service this provider type offers"
                    rows="2"
                    className={formErrors.serviceDescription ? 'error' : ''}
                    disabled={isLoading}
                  />
                  {formErrors.serviceDescription && (
                    <span className="error-message">{formErrors.serviceDescription}</span>
                  )}
                </div>
              </div>

              <div className="checkbox-group">
                <div className="checkbox-item">
                  <label>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    <span>Active</span>
                  </label>
                  <small>Provider type will be available for use</small>
                </div>

                <div className="checkbox-item">
                  <label>
                    <input
                      type="checkbox"
                      name="isServiceActive"
                      checked={formData.isServiceActive}
                      onChange={handleInputChange}
                      disabled={isLoading || !formData.serviceName}
                    />
                    <span>Service Active</span>
                  </label>
                  <small>Service will be available for this provider type</small>
                </div>
              </div>

              {error && (
                <div className="error-message global-error">
                  <strong>Error:</strong> {error}
                </div>
              )}

              {successMessage && (
                <div className="success-message">
                  <strong>Success:</strong> {successMessage}
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowModal(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : 'Create Provider Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateProviderTypeForm;